<template>
  <v-dialog :value="dialog" @input="$emit('update:dialog', $event)" max-width="640" scrollable>
    <v-card class="rounded-xl overflow-hidden">
      <v-toolbar flat color="primary" dark dense>
        <v-icon left small>mdi-help-circle-outline</v-icon>
        <v-toolbar-title class="subtitle-1 font-weight-bold">¿Cómo funciona?</v-toolbar-title>
        <v-spacer />
        <v-btn icon small aria-label="Cerrar ayuda" @click="$emit('update:dialog', false)">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-0" style="max-height: 72vh">
        <v-expansion-panels v-model="abierto" accordion flat class="ayuda-paneles">
          <v-expansion-panel v-for="s in secciones" :key="s.titulo">
            <v-expansion-panel-header class="font-weight-bold">
              <span class="d-flex align-center">
                <v-icon small color="primary" class="mr-3">{{ s.icono }}</v-icon>{{ s.titulo }}
              </span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <ul class="ayuda-pasos">
                <li v-for="(p, i) in s.pasos" :key="i" v-html="p"></li>
              </ul>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
// Ayuda breve de la gestión de activos; enseña solo lo que el usuario puede hacer con sus permisos
export default {
  name: "DialogAyuda",
  props: {
    dialog: { type: Boolean, default: false },
    editor: { type: Boolean, default: false }, // editar-armarios
    editorPlanos: { type: Boolean, default: false }, // editar-planos
    verPlanos: { type: Boolean, default: true },
    puedeAdjuntar: { type: Boolean, default: false },
    esRamos: { type: Boolean, default: false },
  },
  data() {
    return { abierto: 0 };
  },
  computed: {
    secciones() {
      const s = [
        {
          titulo: "Encontrar un armario",
          icono: "mdi-magnify",
          pasos: [
            "Escribe en <b>Buscar armario por nombre</b>: el árbol muestra los que coinciden y los armarios de los que cuelgan.",
            "Con <b>Filtrar por categoría</b> ves solo los armarios de un tipo.",
            "Arrastra el fondo del árbol para moverte y usa los controles de la esquina para acercar, alejar, plegar o desplegar todo.",
            "El botón <b>QR</b> abre la cámara: al leer la etiqueta de un armario se abre directamente su documentación.",
          ],
        },
        {
          titulo: "Ver la documentación",
          icono: "mdi-file-document-multiple-outline",
          pasos: [
            "Pulsa un armario y después el icono de <b>documentos</b> (o el armario en el plano).",
            "El <b>ojo</b> abre el documento sin descargarlo (PDF e imágenes); la <b>flecha</b> lo descarga.",
            ...(this.esRamos
              ? [
                  "El <b>historial</b> (icono del reloj, junto al título) muestra quién ha subido, borrado o cambiado algo y cuándo.",
                  "Los documentos generales del cliente están en el icono de la carpeta, junto al título.",
                ]
              : []),
          ],
        },
      ];
      if (this.verPlanos) {
        s.push({
          titulo: "Moverse por los planos",
          icono: "mdi-floor-plan",
          pasos: [
            "Elige un plano en el panel de la izquierda. Cada armario aparece como una <b>región</b> (rectángulo) o un <b>símbolo</b> con el color de su categoría.",
            "Pasa el ratón por encima para ver el nombre, o activa la <b>etiqueta</b> de los controles para verlos todos.",
            "Haz zoom con la <b>rueda del ratón</b> (o pellizcando en el móvil) y arrastra el fondo para moverte.",
            "La <b>lupa</b> busca un armario en el plano y te lleva hasta él.",
            "En la <b>leyenda</b> pulsa una categoría para ocultarla o mostrarla.",
          ],
        });
      }
      if (this.puedeAdjuntar) {
        s.push({
          titulo: "Subir documentos",
          icono: "mdi-cloud-upload-outline",
          pasos: [
            "En la documentación del armario pulsa <b>Subir archivo</b>.",
            "También puedes <b>arrastrar uno o varios archivos</b> desde tu ordenador y soltarlos sobre la lista: se suben con su nombre.",
          ],
        });
      }
      if (this.editor) {
        s.push({
          titulo: "Organizar los armarios",
          icono: "mdi-file-tree-outline",
          pasos: [
            "Pulsa un armario para ver sus acciones: añadir un hijo, editar, categoría, cambiar de padre, ordenar sus hijos…",
            "Los documentos se guardan en <b>carpetas</b>: HMI / PLC, ESQUEMAS, DOCUMENTACIÓN, FOTOS y OTROS, cada una con un máximo de documentos. En la ventana del armario se eligen en el desplegable «Carpetas» (las que tienen documentos no se pueden quitar), o a varios armarios a la vez con la selección múltiple.",
            "Para subir un documento el armario tiene que tener carpetas. Para actualizar uno, elimina el anterior y sube el nuevo. Los documentos de antes de las carpetas aparecen en «Sin carpeta»: muévelos a la suya.",
            "El panel de calidad de los datos avisa de los armarios con <b>carpetas vacías</b>.",
            "En el menú ⋮ del armario, <b>Añadir nota</b> guarda una nota interna (solo la ve Ramos); el icono amarillo en la tarjeta indica que tiene nota.",
            "Con <b>Selección múltiple</b> (junto al +) marcas varios armarios y les asignas una categoría o los mueves a la vez.",
            "<b>Ordenar armarios principales</b>, en los controles del árbol, cambia el orden de los de primer nivel.",
            "<b>Calidad de los datos</b> (junto al título) enseña qué armarios no tienen documentos, plano o categoría, para ir completándolos.",
          ],
        });
      }
      if (this.editorPlanos) {
        s.push({
          titulo: "Colocar armarios en un plano",
          icono: "mdi-map-marker-plus",
          pasos: [
            "Pulsa <b>Generar Región</b>, elige el armario y decide cómo colocarlo: <b>región</b> (rectángulo que se puede redimensionar y girar) o <b>símbolo</b> (el de su categoría).",
            "Haz clic en el plano donde quieras ponerlo.",
            "En <b>Editar regiones</b> los símbolos se mueven arrastrándolos, tienen un asa para cambiar su tamaño y una ✕ para quitarlos.",
            "El símbolo y el color de cada categoría se eligen en <b>Gestionar categorías</b> (rueda dentada del árbol).",
          ],
        });
      }
      return s;
    },
  },
};
</script>

<style scoped>
.ayuda-paneles {
  padding: 8px;
}
.ayuda-pasos {
  padding-left: 18px;
  font-size: 0.875rem;
  color: #37474f;
}
.ayuda-pasos li + li {
  margin-top: 6px;
}
</style>
